import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import {
  Dialog,
  Drawer,
  useAccountStore,
  useGlobalCopyStore,
  useGoalStore,
  useUserStore,
} from '../../common'
import { type Goal, TrackType } from '../../core'

import { GoalDetails } from './GoalDetails'
import { buildDynamicCopy } from '../../core/utils/Localization'

interface GoalDetailsDrawerProps {
  onClose?: (trackType: TrackType) => void
}

export const GoalDetailsDrawer = observer(({ onClose }: GoalDetailsDrawerProps) => {
  const { visibleRetirementAccounts: retirementAccounts } = useAccountStore()
  const { goals: copy, common: commonCopy } = useGlobalCopyStore()
  const {
    addGoal,
    addRetirementGoal,
    deleteGoal,
    deleteRetirementGoal,
    newRetirementGoalAccounts,
    retirementGoalAccounts,
    selectedGoal,
    setAlert,
    setSelectedGoal,
    updateGoal,
  } = useGoalStore()
  const { user } = useUserStore()

  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false)

  const isNew = !selectedGoal?.guid
  const isComplete =
    selectedGoal?.track_type === TrackType.SAVINGS_TRACK &&
    selectedGoal.is_complete &&
    !selectedGoal.has_been_spent

  const accounts = React.useMemo(() => {
    const goalAccounts = isNew ? newRetirementGoalAccounts : retirementGoalAccounts
    return retirementAccounts.filter((a) => goalAccounts.some((rga) => rga.account_guid === a.guid))
  }, [isNew, retirementAccounts, newRetirementGoalAccounts, retirementGoalAccounts])

  const isValidGoal = React.useMemo(() => {
    if (!selectedGoal) return false

    // Allow deletion of existing goals even if the accounts are not associated with the goals,
    // as long as the goal is not new and not already complete.
    if (!isNew && !isComplete) return true

    const { account_guid, track_type, name, amount, retirement_age } = selectedGoal

    if (track_type === TrackType.SAVINGS_TRACK) {
      if (!account_guid) return false
      return Boolean(name) && Boolean(amount)
    }

    if (track_type === TrackType.RETIREMENT_TRACK) {
      if (!accounts.length) return false
      return Boolean(name) && Boolean(amount) && Boolean(retirement_age) && Boolean(user.birthday)
    }

    return true
  }, [selectedGoal, accounts, user.birthday])

  let primaryText = commonCopy.delete_button
  let primaryAction = () => setIsDeleteOpen(true)
  if (isNew) {
    primaryAction = () => handleSave()
    primaryText = commonCopy.save_button
  } else if (isComplete) {
    primaryAction = () => handleComplete()
    primaryText = copy.mark_as_complete_button
  }

  const handleSave = async () => {
    if (selectedGoal) {
      const trackType = selectedGoal.track_type
      if (trackType !== TrackType.RETIREMENT_TRACK) {
        await addGoal(selectedGoal)
      } else if (trackType === TrackType.RETIREMENT_TRACK) {
        await addRetirementGoal({ ...selectedGoal, target_amount: selectedGoal.amount })
      }
      setAlert(copy.alert_goal_added)
      setSelectedGoal(undefined)
      if (trackType) {
        onClose?.(trackType)
      }
    }
  }

  const handleDelete = async () => {
    if (!selectedGoal) return

    if (selectedGoal.track_type !== TrackType.RETIREMENT_TRACK) {
      await deleteGoal(selectedGoal.guid)
    } else if (selectedGoal.track_type === TrackType.RETIREMENT_TRACK) {
      await deleteRetirementGoal(selectedGoal)
    }
    setAlert(copy.alert_goal_deleted)
    setSelectedGoal(undefined)
  }

  const handleComplete = async () => {
    const updatedGoal = {
      ...selectedGoal,
      has_been_spent: true,
    } as Goal
    await updateGoal(updatedGoal)
    setAlert(copy.alert_goal_completed)
    setSelectedGoal(undefined)
  }

  return (
    <Stack>
      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        hasSecondaryAction={isNew}
        isOpen={Boolean(selectedGoal)}
        isPrimaryDisabled={!isValidGoal}
        onClose={() => setSelectedGoal(undefined)}
        onPrimaryAction={primaryAction}
        primaryColor={isNew || isComplete ? 'primary' : 'error'}
        primaryText={primaryText}
        secondaryColor="primary"
        title={copy.goal_details}
      >
        {selectedGoal && <GoalDetails goal={selectedGoal} />}
      </Drawer>
      <Dialog
        copy={{ close_aria: commonCopy.close_aria, title: copy.delete_goal }}
        isOpen={isDeleteOpen}
        maxWidth="xs"
        onClose={() => setIsDeleteOpen(false)}
        onPrimaryAction={handleDelete}
        primaryColor="error"
        primaryText={commonCopy.delete_button}
      >
        <Text variant="subtitle1">
          {buildDynamicCopy(copy.delete_description, selectedGoal?.name || '') as string}
        </Text>
      </Dialog>
    </Stack>
  )
})
